import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import {
  useImportLocalesMutation,
  useLazyExportLocalesQuery,
  useLazyGetBrandingSettingsQuery,
  useLazyGetFeatureSettingsQuery,
  useLazyGetLocaleNameSpacesQuery,
  useLazyGetLocaleQuery,
  useLazyGetPlatformDocumentationQuery,
  useLazySearchPlatformDocumentationQuery,
  useRestoreBrandingSettingsMutation,
  useSaveBrandingSettingsMutation,
  useSaveFeatureSettingsMutation,
  useSaveLocalesMutation,
  useSaveProductDefinitionsMutation,
} from '../api/platform.api';
import {
  defaultDocumentationBreadcrumb,
  setBrandingSettings,
  setCurrentDocumentationArticles,
  setCurrentDocumentationCategories,
  setCurrentDocumentationContent,
  setCurrentDocumentationTitle,
  setDocumentationBreadcrumb,
  setFeatureSettings,
  setIsLoading,
  setNavigationLinks,
} from '../store/platform.slice';
import {
  GetLocalePayload,
  IBrandingSettings,
  IFeatureSettings,
  IGetPlatformDocumentationPayload,
  ILocale,
  ILocaleNameSpace,
  ISearchPlatformDocumentation,
  ISearchPlatformDocumentationPayload,
  ImportLocalesPayload,
  SaveBrandingSettingsPayload,
  SaveFeatureSettingsPayload,
  SaveLocalesPayload,
  SaveProductDefinitionsPayload,
} from '../types/platform.types';

interface UsePlatform {
  saveFeatureSettingsHandler: (payload: SaveFeatureSettingsPayload) => Promise<IFeatureSettings | null>;
  fetchFeatureSettingsHandler: () => Promise<void>;
  getPlatformDocumentationsHandler: (payload: IGetPlatformDocumentationPayload) => Promise<void>;
  searchPlatformDocumentationsHandler: (
    payload: ISearchPlatformDocumentationPayload,
  ) => Promise<ISearchPlatformDocumentation[]>;
  fetchBrandingSettingsHandler: () => Promise<boolean>;
  saveBrandingSettingsHandler: (payload: SaveBrandingSettingsPayload) => Promise<IBrandingSettings | null>;
  restoreBrandingSettingsHandler: () => Promise<void>;
  getLocaleNameSpacesHandler: () => Promise<ILocaleNameSpace[] | null>;
  getLocaleHandler: (payload: GetLocalePayload) => Promise<ILocale[] | null>;
  exportLocalesHandler: () => Promise<string | null>;
  importLocalesHandler: (payload: ImportLocalesPayload) => Promise<boolean>;
  saveLocalesHandler: (payload: SaveLocalesPayload) => Promise<boolean>;
  saveProductDefinitionsHandler: (payload: SaveProductDefinitionsPayload) => Promise<IFeatureSettings | null>;
}

export const usePlatform = (): UsePlatform => {
  const dispatch = useAppDispatch();
  const { navigationLinks } = useAppSelector((state) => state.platform);
  const { currentUser } = useAppSelector((state) => state.authSession);

  const [saveFeatureSettingsQuery] = useSaveFeatureSettingsMutation();
  const [getFeatureSettingsQuery] = useLazyGetFeatureSettingsQuery();
  const [getPlatformDocumentationsQuery] = useLazyGetPlatformDocumentationQuery();
  const [searchPlatformDocumentationsQuery] = useLazySearchPlatformDocumentationQuery();
  const [getBrandingSettingsQuery] = useLazyGetBrandingSettingsQuery();
  const [saveBrandingSettingsQuery] = useSaveBrandingSettingsMutation();
  const [restoreBrandingSettingsQuery] = useRestoreBrandingSettingsMutation();
  const [getLocaleNameSpacesQuery] = useLazyGetLocaleNameSpacesQuery();
  const [getLocaleQuery] = useLazyGetLocaleQuery();
  const [exportLocalesQuery] = useLazyExportLocalesQuery();
  const [importLocalesQuery] = useImportLocalesMutation();
  const [saveLocalesQuery] = useSaveLocalesMutation();
  const [saveProductDefinitionsQuery] = useSaveProductDefinitionsMutation();

  const searchPlatformDocumentationsHandler = async (payload: ISearchPlatformDocumentationPayload) => {
    const { data: response, error } = await searchPlatformDocumentationsQuery(payload);
    if (error || !response?.data) {
      return [];
    }
    return response?.data;
  };

  const getPlatformDocumentationsHandler = async (payload: IGetPlatformDocumentationPayload) => {
    dispatch(setIsLoading(true));
    const { data: response, error } = await getPlatformDocumentationsQuery(payload);

    if (error && 'status' in error && error.status === 404) {
      const newRouter = [...defaultDocumentationBreadcrumb, ...[{ name: 'Not Found', slug: '404', isCurrent: true }]];
      batch(() => {
        dispatch(
          setDocumentationBreadcrumb(
            newRouter.map((route, index) => {
              if (index === newRouter.length - 1) {
                return {
                  ...route,
                  isCurrent: true,
                };
              }
              return {
                ...route,
                isCurrent: false,
              };
            }),
          ),
        );
        dispatch(setCurrentDocumentationContent('Not Found'));
        dispatch(setCurrentDocumentationTitle('Platform Documentation'));
        dispatch(setIsLoading(false));
      });

      return;
    }
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to get platform documentation');
      dispatch(setIsLoading(false));
      return;
    }
    batch(() => {
      const newRouter = [...defaultDocumentationBreadcrumb, ...response.data.breadcrumbs];
      dispatch(
        setDocumentationBreadcrumb(
          newRouter.map((route, index) => {
            if (index === newRouter.length - 1) {
              return {
                ...route,
                isCurrent: true,
              };
            }
            return {
              ...route,
              isCurrent: false,
            };
          }),
        ),
      );
      dispatch(setCurrentDocumentationTitle(response.data.title || 'Platform Documentation'));
      dispatch(setCurrentDocumentationContent(response.data.content));
      dispatch(setCurrentDocumentationArticles(response.data.articles));
      dispatch(setCurrentDocumentationCategories(response.data.categories));
    });
    dispatch(setIsLoading(false));
    return;
  };

  const saveFeatureSettingsHandler = async (payload: SaveFeatureSettingsPayload) => {
    dispatch(setIsLoading(true));
    const { data: response, error } = await saveFeatureSettingsQuery(payload);
    dispatch(setIsLoading(false));

    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to save feature settings');
      return null;
    }

    if (response.data.disabled_pages) {
      const disabledPages = response.data.disabled_pages;
      dispatch(
        setNavigationLinks(navigationLinks.map((link) => ({ ...link, disabled: disabledPages.includes(link.to) }))),
      );
    }

    dispatch(setFeatureSettings(response.data));
    toast.success('Settings saved');
    return response.data;
  };

  const saveProductDefinitionsHandler = async (payload: SaveProductDefinitionsPayload) => {
    dispatch(setIsLoading(true));
    const { data: response, error } = await saveProductDefinitionsQuery(payload);
    dispatch(setIsLoading(false));

    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to save');
      return null;
    }

    dispatch(setFeatureSettings(response.data));
    toast.success('Saved');
    return response.data;
  };

  const fetchFeatureSettingsHandler = async () => {
    const { data: response, error } = await getFeatureSettingsQuery();

    if (error || !response?.data) {
      dispatch(setNavigationLinks([]));
      return;
    }

    if (response.data.disabled_pages) {
      const disabledPages = response.data.disabled_pages;
      dispatch(
        setNavigationLinks(
          navigationLinks.map((link) => ({
            ...link,
            disabled:
              disabledPages.includes(link.to) ||
              (link.to === '/platform-dashboard' &&
                currentUser?.company.id !== response.data?.main_company?.id &&
                !currentUser?.hasAdminAccess),
          })),
        ),
      );
    }

    dispatch(setFeatureSettings(response.data));
  };

  const fetchBrandingSettingsHandler = async () => {
    dispatch(setIsLoading(true));
    const { data: response, error } = await getBrandingSettingsQuery();
    dispatch(setIsLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to get branding settings');
      return false;
    }
    dispatch(setBrandingSettings(response.data));
    return true;
  };

  const saveBrandingSettingsHandler = async (payload: SaveBrandingSettingsPayload) => {
    const { data: response, error } = await saveBrandingSettingsQuery(payload);
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to save branding settings');
      return null;
    }
    dispatch(setBrandingSettings(response.data));
    return response.data;
  };

  const restoreBrandingSettingsHandler = async () => {
    const { data: response, error } = await restoreBrandingSettingsQuery();
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to restore branding settings');
      return;
    }
    dispatch(setBrandingSettings(response.data));
    toast.success('Branding settings restored');
  };

  const getLocaleNameSpacesHandler = async () => {
    const { data: response, error } = await getLocaleNameSpacesQuery();
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to get locale name spaces');
      return null;
    }
    return response.data;
  };

  const getLocaleHandler = async (payload: GetLocalePayload) => {
    const { data: response, error } = await getLocaleQuery(payload);
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to get locale');
      return null;
    }
    return response.data;
  };

  const exportLocalesHandler = async () => {
    const { data: response, error } = await exportLocalesQuery();
    if (error || !response) {
      toast.error(getApiErrorMessage(error) || 'Failed to export locales');
      return null;
    }
    return response;
  };

  const importLocalesHandler = async (payload: ImportLocalesPayload) => {
    const { error } = await importLocalesQuery(payload);
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to import locales');
      return false;
    }
    toast.success('Locales imported successfully!');
    return true;
  };

  const saveLocalesHandler = async (payload: SaveLocalesPayload) => {
    const { error } = await saveLocalesQuery(payload);
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to save locales');
      return false;
    }
    toast.success('Locales saved successfully!');
    return true;
  };

  return {
    saveFeatureSettingsHandler,
    fetchFeatureSettingsHandler,
    getPlatformDocumentationsHandler,
    searchPlatformDocumentationsHandler,
    fetchBrandingSettingsHandler,
    saveBrandingSettingsHandler,
    restoreBrandingSettingsHandler,
    getLocaleNameSpacesHandler,
    getLocaleHandler,
    exportLocalesHandler,
    importLocalesHandler,
    saveLocalesHandler,
    saveProductDefinitionsHandler,
  };
};
